import React, { useState, useMemo } from 'react';
import './ETFOverlap.css';

// Top holdings for common ETFs (top 15 by weight)
const ETF_HOLDINGS = {
  SPY: { name: 'S&P 500', holdings: ['AAPL','MSFT','NVDA','AMZN','META','GOOGL','AVGO','BRK.B','LLY','JPM','TSLA','V','UNH','XOM','MA'] },
  QQQ: { name: 'Nasdaq 100', holdings: ['AAPL','MSFT','NVDA','AMZN','META','AVGO','GOOGL','COST','TSLA','NFLX','AMD','ADBE','LIN','ISRG','QCOM'] },
  VOO: { name: 'Vanguard S&P 500', holdings: ['AAPL','MSFT','NVDA','AMZN','META','GOOGL','AVGO','BRK.B','LLY','JPM','TSLA','V','UNH','XOM','MA'] },
  VTI: { name: 'Total US Market', holdings: ['AAPL','MSFT','NVDA','AMZN','META','GOOGL','AVGO','BRK.B','LLY','JPM','TSLA','V','UNH','XOM','MA'] },
  IWM: { name: 'Russell 2000', holdings: ['SMCI','FTAI','SPR','CARG','FN','DUOL','RKLB','TNET','CRS','PIPR','ONTO','DOCS','CEIX','ACIW','CBT'] },
  DIA: { name: 'Dow Jones', holdings: ['GS','UNH','MSFT','HD','CAT','AMGN','MCD','V','AXP','CRM','AAPL','TRV','JPM','IBM','AMZN'] },
  SMH: { name: 'Semiconductors', holdings: ['NVDA','TSM','AVGO','AMD','QCOM','TXN','AMAT','MU','ASML','INTC','LRCX','ADI','KLAC','MRVL','NXPI'] },
  SOXX: { name: 'Semiconductors iShares', holdings: ['NVDA','AVGO','AMD','QCOM','TXN','AMAT','MU','ASML','INTC','LRCX','ADI','KLAC','MRVL','NXPI','MCHP'] },
  XLK: { name: 'Technology Select', holdings: ['AAPL','MSFT','NVDA','AVGO','ORCL','CRM','AMD','ADBE','CSCO','ACN','QCOM','TXN','INTC','INTU','AMAT'] },
  XLF: { name: 'Financial Select', holdings: ['JPM','BAC','WFC','GS','MS','SPGI','BLK','SCHW','C','PGR','CME','ICE','CB','MMC','AXP'] },
  XLE: { name: 'Energy Select', holdings: ['XOM','CVX','COP','SLB','EOG','MPC','WMB','PSX','PXD','VLO','OXY','HES','KMI','HAL','DVN'] },
  XLV: { name: 'Healthcare Select', holdings: ['LLY','UNH','JNJ','ABBV','MRK','TMO','ABT','ISRG','AMGN','PFE','DHR','SYK','BSX','BMY','VRTX'] },
  ARKK: { name: 'ARK Innovation', holdings: ['TSLA','COIN','PLTR','RKLB','SQ','ROKU','PATH','HOOD','TWLO','U','DKNG','CRSP','DNA','BEAM','NTLA'] },
  GLD: { name: 'Gold Trust', holdings: [] },
  EWJ: { name: 'Japan ETF', holdings: [] },
  COPX: { name: 'Copper Miners', holdings: ['FCX','SCCO','TECK','IVN','ANTO','FM','HBM','CS','ERO','CPER','NEXA','TRQ','OZL','EDR','WPM'] },
};

export default function ETFOverlap({ portfolioItems }) {
  const [collapsed, setCollapsed] = useState(false);

  const analysis = useMemo(() => {
    if (!portfolioItems || portfolioItems.length === 0) return null;

    const symbols = portfolioItems.map(p => p.symbol.toUpperCase());
    const etfsInPortfolio = symbols.filter(s => ETF_HOLDINGS[s]);
    const stocksInPortfolio = symbols.filter(s => !ETF_HOLDINGS[s]);

    // Even if no ETFs in portfolio, check if individual stocks appear in common ETFs

    // Find overlapping holdings between ETFs
    const etfOverlaps = [];
    for (let i = 0; i < etfsInPortfolio.length; i++) {
      for (let j = i + 1; j < etfsInPortfolio.length; j++) {
        const etf1 = etfsInPortfolio[i];
        const etf2 = etfsInPortfolio[j];
        const h1 = new Set(ETF_HOLDINGS[etf1]?.holdings || []);
        const h2 = new Set(ETF_HOLDINGS[etf2]?.holdings || []);
        const overlap = [...h1].filter(s => h2.has(s));
        if (overlap.length > 0) {
          const overlapPct = Math.round((overlap.length / Math.min(h1.size, h2.size)) * 100);
          etfOverlaps.push({
            etf1,
            etf2,
            etf1Name: ETF_HOLDINGS[etf1]?.name || etf1,
            etf2Name: ETF_HOLDINGS[etf2]?.name || etf2,
            overlap,
            overlapPct,
          });
        }
      }
    }

    // Always check stocks against major ETFs (user's ETFs + common ones)
    const majorETFs = ['SPY', 'QQQ', 'DIA', 'SMH', 'SOXX', 'XLK', 'XLF', 'XLE', 'XLV', 'ARKK'];
    const etfsToCheck = [...new Set([...etfsInPortfolio, ...majorETFs])];
    const stockEtfOverlaps = [];
    stocksInPortfolio.forEach(stock => {
      const containedIn = etfsToCheck.filter(etf =>
        (ETF_HOLDINGS[etf]?.holdings || []).includes(stock)
      );
      if (containedIn.length > 0) {
        stockEtfOverlaps.push({ stock, etfs: containedIn });
      }
    });

    // Calculate concentration score
    const allHoldings = new Map(); // stock -> count of ETFs holding it
    etfsToCheck.forEach(etf => {
      (ETF_HOLDINGS[etf]?.holdings || []).forEach(h => {
        allHoldings.set(h, (allHoldings.get(h) || 0) + 1);
      });
    });
    stocksInPortfolio.forEach(s => {
      allHoldings.set(s, (allHoldings.get(s) || 0) + 1);
    });

    const concentrated = [...allHoldings.entries()]
      .filter(([, count]) => count >= 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    return { etfOverlaps, stockEtfOverlaps, concentrated, etfsInPortfolio, hasData: etfOverlaps.length > 0 || stockEtfOverlaps.length > 0 || concentrated.length > 0 };
  }, [portfolioItems]);

  if (!analysis) return null;

  if (!analysis.hasData) {
    return (
      <div className="etf-overlap">
        <div className="etf-overlap-header">
          <h3 className="etf-overlap-title">ETF Overlap Analysis</h3>
        </div>
        <p style={{ color: '#64748B', fontSize: '0.82rem', margin: 0 }}>
          No ETF overlap detected in your portfolio. This section shows when your holdings appear in major ETFs (SPY, QQQ, SMH, etc.).
        </p>
      </div>
    );
  }

  return (
    <div className="etf-overlap">
      <div className="etf-overlap-header">
        <h3 className="etf-overlap-title">ETF Overlap Analysis</h3>
        <button className="etf-overlap-toggle" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* ETF-to-ETF overlap */}
          {analysis.etfOverlaps.length > 0 && (
            <div className="etf-overlap-section">
              <h4 className="etf-section-label">ETF-to-ETF Overlap</h4>
              <div className="etf-overlap-cards">
                {analysis.etfOverlaps.map((o, i) => (
                  <div key={i} className="etf-overlap-card">
                    <div className="etf-pair">
                      <span className="etf-name">{o.etf1}</span>
                      <span className="etf-vs">×</span>
                      <span className="etf-name">{o.etf2}</span>
                    </div>
                    <div className={`etf-overlap-pct ${o.overlapPct >= 50 ? 'high' : o.overlapPct >= 25 ? 'medium' : 'low'}`}>
                      {o.overlapPct}% overlap
                    </div>
                    <div className="etf-overlap-stocks">
                      {o.overlap.slice(0, 8).map(s => (
                        <span key={s} className="etf-stock-chip">{s}</span>
                      ))}
                      {o.overlap.length > 8 && <span className="etf-stock-more">+{o.overlap.length - 8}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stocks you hold directly that are in your ETFs */}
          {analysis.stockEtfOverlaps.length > 0 && (
            <div className="etf-overlap-section">
              <h4 className="etf-section-label">{analysis.etfsInPortfolio.length > 0 ? 'Direct Holdings in Your ETFs' : 'Your Holdings in Major ETFs'}</h4>
              <p className="etf-section-desc">{analysis.etfsInPortfolio.length > 0 ? 'These stocks you hold individually are also in your ETF positions, doubling your exposure.' : 'Your individual stocks also appear in these major ETFs.'}</p>
              <div className="etf-direct-list">
                {analysis.stockEtfOverlaps.map((o, i) => (
                  <div key={i} className="etf-direct-row">
                    <span className="etf-direct-stock">{o.stock}</span>
                    <span className="etf-direct-in">also in</span>
                    <div className="etf-direct-etfs">
                      {o.etfs.map(e => <span key={e} className="etf-stock-chip">{e}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Most concentrated positions */}
          {analysis.concentrated.length > 0 && (
            <div className="etf-overlap-section">
              <h4 className="etf-section-label">Most Concentrated Positions</h4>
              <p className="etf-section-desc">Stocks that appear across multiple holdings (direct + ETFs).</p>
              <div className="etf-concentrated">
                {analysis.concentrated.map(([stock, count]) => (
                  <div key={stock} className="etf-conc-item">
                    <span className="etf-conc-stock">{stock}</span>
                    <span className={`etf-conc-count ${count >= 3 ? 'high' : ''}`}>{count}× exposure</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
